import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { setPageMeta } from "@/utils/seo";
import {
  defaultFilters,
  filterGroups,
  filterMovies,
  type FilterState,
} from "@/utils/filterMovies";

const PAGE_SIZE = 8;

const TYPE_MAP: Record<string, { type: "电视剧" | "电影"; name: string; intro: string }> = {
  drama: {
    type: "电视剧",
    name: "日韩电视剧",
    intro: "精选日韩热门剧集，全程中文字幕，按地区、年份与评分筛选，快速找到想追的剧。",
  },
  movie: {
    type: "电影",
    name: "日韩电影",
    intro: "汇集日韩口碑电影，覆盖爱情、剧情、惊悚与动画，配中文字幕，随开随看。",
  },
};

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: PAGE_SIZE }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="aspect-[2/3] w-full rounded-lg bg-background-200" />
          <div className="mt-3 h-4 w-3/4 rounded bg-background-200" />
          <div className="mt-2 h-3 w-1/2 rounded bg-background-200" />
        </div>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const { type: typeKey } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggle } = useFavorites();
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const meta = typeKey ? TYPE_MAP[typeKey] : undefined;

  useEffect(() => {
    if (meta) {
      setPageMeta(
        `${meta.name}－日韩中文字幕在线观看`,
        `${meta.intro} 支持地区、年份、评分筛选，中文字幕观看更省心。`,
      );
    }
  }, [meta]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [filters, page, typeKey]);

  const result = useMemo(() => {
    if (!meta) return [];
    return filterMovies(movies, filters, meta.type);
  }, [meta, filters]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const pageItems = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const groups: FilterGroup[] = meta
    ? [
        {
          key: "type",
          label: "类型",
          options: [
            { label: "电视剧", value: "drama" },
            { label: "电影", value: "movie" },
          ],
        },
        ...filterGroups,
      ]
    : filterGroups;

  const handleChange = (key: string, value: string) => {
    if (key === "type") {
      navigate(`/category/${value}`);
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  if (!meta) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-2xl" />
          </span>
          <h1 className="mt-5 text-xl font-medium text-foreground-950">分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">请从导航栏选择电视剧或电影分类。</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600"
          >
            返回首页
          </button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const filterValue: Record<string, string> = { ...filters, type: typeKey as string };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: meta.name },
            ]}
          />
          <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h1 className="flex items-center gap-3 text-xl font-medium text-foreground-950 md:text-2xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className={typeKey === "drama" ? "ri-tv-2-line text-lg" : "ri-film-line text-lg"} />
              </span>
              {meta.name}
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
              {meta.intro}
            </p>
            <p className="mt-3 text-xs text-foreground-500">
              共 <span className="font-medium text-accent-600">{result.length}</span> 部符合条件的影视
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            <aside className="w-full shrink-0 lg:w-64">
              <FilterPanel
                groups={groups}
                value={filterValue}
                onChange={handleChange}
                onReset={handleReset}
              />
            </aside>

            <div className="min-w-0 flex-1">
              {loading ? (
                <SkeletonGrid />
              ) : (
                <MovieGrid
                  movies={pageItems}
                  emptyText="未找到符合条件的影视内容"
                  onReset={handleReset}
                  isFavorite={isFavorite}
                  onToggleFavorite={toggle}
                />
              )}
              {!loading && (
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}